import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminLayout } from './features/admin/AdminLayout';
import { AdminOverview } from './features/admin/Overview';
import { AdminUsers } from './features/admin/Users';
import { AiDude } from './features/ag5/AiDude';
import { Ag5Layout } from './features/ag5/Ag5Layout';
import { UsefulLinks } from './features/ag5/Links';
import { ScriptPage } from './features/ag5/ScriptPage';
import { Ag5ScriptList } from './features/ag5/ScriptList';
import { AG5_SCRIPTS } from './features/ag5/scripts';
import { BrainLayout } from './features/brain/BrainLayout';
import { BrainAsk } from './features/brain/Ask';
import { BrainNotes } from './features/brain/Notes';
import { BoxerLayout } from './features/boxer/BoxerLayout';
import { BoxerPage } from './features/boxer/BoxerPage';
import { CaloriesDay } from './features/calories/Day';
import { CaloriesDishes } from './features/calories/Dishes';
import { CaloriesLayout } from './features/calories/CaloriesLayout';
import { CaloriesProducts } from './features/calories/Products';
import { CaloriesProgress } from './features/calories/Progress';
import { CaloriesSettings } from './features/calories/Settings';
import {
  ChecklistEdit,
  ChecklistNew,
} from './features/checklists/ChecklistForm';
import { ChecklistGroups } from './features/checklists/ChecklistGroups';
import { ChecklistOverview } from './features/checklists/ChecklistOverview';
import { ChecklistsLayout } from './features/checklists/ChecklistsLayout';
import { ChecklistView } from './features/checklists/ChecklistView';
import { FinancesLayout } from './features/finances/FinancesLayout';
import { Goals } from './features/finances/Goals';
import { FinancesOverview } from './features/finances/Overview';
import { Wallets } from './features/finances/Wallets';
import { LibraryComponentList } from './features/library/ComponentList';
import { LibraryHome } from './features/library/Home';
import { LibraryLayout } from './features/library/LibraryLayout';
import { StoryPage } from './features/library/StoryPage';
import { STORIES } from './features/library/stories';
import { RecipeEdit, RecipeNew } from './features/recipes/RecipeForm';
import { RecipeList } from './features/recipes/RecipeList';
import { RecipesLayout } from './features/recipes/RecipesLayout';
import { RecipeView } from './features/recipes/RecipeView';
import { AppLayout } from './layouts/AppLayout';
import { Apps } from './routes/Apps';
import { Landing } from './routes/Landing';
import { Login } from './routes/Login';
import { NotFound } from './routes/NotFound';
import { Profile } from './routes/Profile';
import {
  NotFoundFrame,
  RequireAnonymous,
  RequireApp,
  RequireAuth,
} from './routes/guards';

/**
 * Route table. Paths match the Nest routes one-for-one so existing links,
 * bookmarks and muscle memory keep working.
 */
export function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireAnonymous>
            <Landing />
          </RequireAnonymous>
        }
      />
      <Route
        path="/login"
        element={
          <RequireAnonymous>
            <Login />
          </RequireAnonymous>
        }
      />

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route path="/apps" element={<Apps />} />
          <Route path="/profile" element={<Profile />} />

          <Route element={<RequireApp app="finances" />}>
            <Route path="/finances" element={<FinancesLayout />}>
              <Route index element={<FinancesOverview />} />
              <Route
                path="overview"
                element={<Navigate to="/finances" replace />}
              />
              <Route path="wallets" element={<Wallets />} />
              <Route path="goals" element={<Goals />} />
            </Route>
          </Route>

          <Route element={<RequireApp app="brain" />}>
            <Route path="/brain" element={<BrainLayout />}>
              <Route index element={<BrainAsk />} />
              <Route path="notes" element={<BrainNotes />} />
            </Route>
          </Route>

          <Route element={<RequireApp app="calories" />}>
            <Route path="/calories" element={<CaloriesLayout />}>
              <Route index element={<CaloriesDay />} />
              <Route path="days/:date" element={<CaloriesDay />} />
              <Route path="products" element={<CaloriesProducts />} />
              <Route path="dishes" element={<CaloriesDishes />} />
              <Route path="progress" element={<CaloriesProgress />} />
              <Route path="settings" element={<CaloriesSettings />} />
            </Route>
          </Route>

          <Route element={<RequireApp app="boxer" />}>
            <Route path="/boxer" element={<BoxerLayout />}>
              <Route index element={<Navigate to="/boxer/modular" replace />} />
              <Route
                path="modular"
                element={<BoxerPage key="modular" type="modular" />}
              />
              <Route
                path="custom"
                element={<BoxerPage key="custom" type="custom" />}
              />
            </Route>
          </Route>

          <Route element={<RequireApp app="recipes" />}>
            <Route path="/recipes" element={<RecipesLayout />}>
              <Route index element={<RecipeList />} />
              <Route path="new" element={<RecipeNew />} />
              <Route path=":id" element={<RecipeView />} />
              <Route path=":id/edit" element={<RecipeEdit />} />
            </Route>
          </Route>

          <Route element={<RequireApp app="checklists" />}>
            <Route path="/checklists" element={<ChecklistsLayout />}>
              <Route index element={<ChecklistOverview />} />
              <Route path="new" element={<ChecklistNew />} />
              <Route path="groups" element={<ChecklistGroups />} />
              <Route path=":id" element={<ChecklistView />} />
              <Route path=":id/edit" element={<ChecklistEdit />} />
            </Route>
          </Route>

          <Route element={<RequireApp app="library" />}>
            <Route path="/library" element={<LibraryLayout />}>
              <Route index element={<LibraryHome />} />
              <Route path="components" element={<LibraryComponentList />} />
              {STORIES.map(({ slug, story }) => (
                <Route
                  key={slug}
                  path={slug}
                  element={<StoryPage story={story} />}
                />
              ))}
            </Route>
          </Route>

          <Route element={<RequireApp app="ag5" />}>
            <Route path="/ag5" element={<Ag5Layout />}>
              <Route index element={<Ag5ScriptList />} />
              <Route path="ai-dude" element={<AiDude />} />
              <Route path="links" element={<UsefulLinks />} />
              {AG5_SCRIPTS.map((script) => (
                <Route
                  key={script.slug}
                  path={`scripts/${script.slug}`}
                  element={<ScriptPage script={script} />}
                />
              ))}
            </Route>
          </Route>

          <Route element={<RequireApp app="admin" />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverview />} />
              <Route
                path="overview"
                element={<Navigate to="/admin" replace />}
              />
              <Route path="users" element={<AdminUsers />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Ranked below every other route, so it only catches what nothing
          else matches — including unknown paths under an app. */}
      <Route element={<NotFoundFrame />}>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
